import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import { useCMS } from '@/hooks/useCMS';

interface SubItem { label: string; href: string; image?: string; }
interface NavCategory { label: string; href: string; subs: SubItem[]; }

const DEFAULT = {
  logo_dark: 'https://public.readdy.ai/ai/img_res/9388341c-4b70-4808-81a9-fd73cbb01c05.png',
  logo_light: 'https://public.readdy.ai/ai/img_res/6ba60f11-c5c2-47eb-b5cf-af4c07ec88c0.png',
  drawer_logo: 'https://public.readdy.ai/ai/img_res/9388341c-4b70-4808-81a9-fd73cbb01c05.png',
  phone_office: '0322 570 505',
  phone_mobile: '599 87 20 20',
  phone_office_href: 'tel:+995322570505',
  phone_mobile_href: 'tel:+995599872020',
  label_categories: 'კატეგორიები',
  label_all: 'ყველა',
  label_subcats: 'ქვეკატეგორიები',
  label_cart: 'კალათა',
  label_close: 'დახურვა',
  label_nav: 'ნავიგაცია',
  page_links: [
    { label: 'მთავარი',  path: '/' },
    { label: 'მაღაზია',  path: '/shop' },
    { label: 'ბრენდები', path: '/brands' },
    { label: 'ბლოგი',    path: '/blog' },
    { label: 'კონტაქტი', path: '/contact' },
  ],
  nav_categories: [
    { label: 'ბუნებრივი ქვა', href: 'https://phoenix.ge/ბუნებრივი-ქვა/', subs: [
      { label: 'მარმარილო', href: 'https://phoenix.ge/ბუნებრივი-ქვა/მარმარილო/', image: 'https://phoenix.ge/wp-content/uploads/2024/06/bunebrivi-qva-მარმარილო04-400x300.png' },
    ]},
  ] as NavCategory[],
};

// ─── Mega Panel ────────────────────────────────────────────────────────────────
function MegaPanel({ open, activeCat, categories, onCatHover, onClose, navbarHeight, labelAll, labelSubcats }: {
  open: boolean; activeCat: NavCategory; categories: NavCategory[];
  onCatHover: (cat: NavCategory) => void; onClose: () => void;
  navbarHeight: number; labelAll: string; labelSubcats: string;
}) {
  const maxPanelHeight = `calc(100vh - ${navbarHeight}px - 16px)`;
  return (
    <div
      className={`absolute left-0 right-0 bg-white border-b border-[#1C1C1C]/8 z-40 transition-all duration-300 shadow-lg ${open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'}`}
      style={{ top: '100%' }}
      onMouseLeave={onClose}
    >
      <div className="flex overflow-hidden" style={{ maxHeight: maxPanelHeight }}>
        {/* Left sidebar */}
        <div className="w-52 flex-shrink-0 bg-[#F8F6F3] border-r border-[#1C1C1C]/6 overflow-y-auto py-2">
          {categories.map((cat) => (
            <a key={cat.label} href={cat.href} target="_blank" rel="noopener noreferrer"
              onMouseEnter={() => onCatHover(cat)}
              className={`flex items-center justify-between px-4 py-2.5 cursor-pointer transition-all duration-150 group ${activeCat.label === cat.label ? 'bg-white text-[#ec3145]' : 'text-[#1C1C1C]/60 hover:bg-white hover:text-[#1C1C1C]'}`}
            >
              <span className="text-[12px] font-semibold leading-tight">{cat.label}</span>
              <i className={`ri-arrow-right-s-line text-sm flex-shrink-0 transition-opacity ${activeCat.label === cat.label ? 'opacity-100 text-[#ec3145]' : 'opacity-0 group-hover:opacity-40'}`}></i>
            </a>
          ))}
        </div>
        {/* Right panel */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-base font-bold text-[#1C1C1C]">{activeCat.label}</h3>
              <p className="text-[10px] text-[#1C1C1C]/35 mt-0.5 tracking-wide">{labelSubcats}</p>
            </div>
            <a href={activeCat.href} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.18em] uppercase text-[#ec3145] hover:text-[#c9283a] transition-colors cursor-pointer whitespace-nowrap">
              {labelAll}<i className="ri-arrow-right-line"></i>
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {activeCat.subs.map((sub) => (
              <a key={sub.label} href={sub.href} target="_blank" rel="noopener noreferrer" className="group flex flex-col gap-2 cursor-pointer">
                {sub.image && (
                  <div className="w-full overflow-hidden bg-[#F5F2EE]" style={{ aspectRatio: '4/3' }}>
                    <img src={sub.image} alt={sub.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                )}
                <span className="text-[11px] font-semibold text-[#1C1C1C]/65 group-hover:text-[#ec3145] transition-colors duration-200 leading-snug">{sub.label}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Mobile Drawer ──────────────────────────────────────────────────────────────
function MobileDrawer({ open, onClose, openCat, setOpenCat, onCartOpen, totalItems, cms, categories, pageLinks }: {
  open: boolean; onClose: () => void; openCat: string | null;
  setOpenCat: (v: string | null) => void; onCartOpen: () => void;
  totalItems: number; cms: typeof DEFAULT; categories: NavCategory[]; pageLinks: typeof DEFAULT.page_links;
}) {
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      <div className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 lg:hidden ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={onClose} />
      <div className={`fixed top-0 right-0 bottom-0 w-[85vw] max-w-sm bg-white z-50 flex flex-col transition-transform duration-400 ease-in-out lg:hidden ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1C1C1C]/8 flex-shrink-0">
          <img src={cms.drawer_logo} alt="Phoenix" className="h-7 w-auto object-contain" />
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center text-[#1C1C1C]/40 hover:text-[#ec3145] transition-colors cursor-pointer" aria-label={cms.label_close}>
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-5 py-4 border-b border-[#1C1C1C]/6">
            <p className="text-[9px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/25 mb-3">{cms.label_nav}</p>
            <div className="flex flex-col gap-0.5">
              {pageLinks.map((link) => (
                <Link key={link.label} to={link.path} onClick={onClose}
                  className="flex items-center gap-3 py-2.5 text-sm font-semibold text-[#1C1C1C]/65 hover:text-[#ec3145] transition-colors cursor-pointer">
                  <i className="ri-arrow-right-s-line text-[#ec3145] text-xs"></i>{link.label}
                </Link>
              ))}
            </div>
          </div>
          <div className="px-5 py-4">
            <p className="text-[9px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/25 mb-3">{cms.label_categories}</p>
            <div className="flex flex-col">
              {categories.map((cat) => (
                <div key={cat.label} className="border-b border-[#1C1C1C]/5 last:border-0">
                  <button className="w-full flex items-center justify-between py-3 cursor-pointer"
                    onClick={() => setOpenCat(openCat === cat.label ? null : cat.label)}>
                    <span className="text-sm font-semibold text-[#1C1C1C]/70 text-left">{cat.label}</span>
                    <i className={`ri-arrow-down-s-line text-[#1C1C1C]/30 flex-shrink-0 transition-transform duration-200 ${openCat === cat.label ? 'rotate-180' : ''}`}></i>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openCat === cat.label ? 'max-h-[500px]' : 'max-h-0'}`}>
                    <div className="pb-3 pl-3 flex flex-col gap-0.5">
                      {cat.subs.map((sub) => (
                        <a key={sub.label} href={sub.href} target="_blank" rel="noopener noreferrer" onClick={onClose}
                          className="flex items-center gap-2 py-2 text-xs text-[#1C1C1C]/50 hover:text-[#ec3145] transition-colors cursor-pointer">
                          <span className="w-1 h-1 rounded-full bg-[#ec3145]/40 flex-shrink-0"></span>{sub.label}
                        </a>
                      ))}
                      <a href={cat.href} target="_blank" rel="noopener noreferrer" onClick={onClose}
                        className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.15em] uppercase text-[#ec3145] mt-1.5 ml-3">
                        {cms.label_all} <i className="ri-arrow-right-line"></i>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="flex-shrink-0 border-t border-[#1C1C1C]/8 px-5 py-4 bg-[#F8F6F3]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex flex-col gap-1.5">
              <a href={cms.phone_office_href} className="flex items-center gap-2 text-xs text-[#1C1C1C]/55 cursor-pointer">
                <i className="ri-phone-line text-[#ec3145] text-xs"></i>{cms.phone_office}
              </a>
              <a href={cms.phone_mobile_href} className="flex items-center gap-2 text-xs text-[#1C1C1C]/55 cursor-pointer">
                <i className="ri-smartphone-line text-[#ec3145] text-xs"></i>+995 {cms.phone_mobile}
              </a>
            </div>
            <button onClick={() => { onCartOpen(); onClose(); }}
              className="relative flex items-center gap-2 bg-[#ec3145] text-white text-xs font-bold px-4 py-2.5 cursor-pointer whitespace-nowrap">
              <i className="ri-shopping-bag-line"></i>
              {cms.label_cart}
              {totalItems > 0 && (
                <span className="w-4 h-4 rounded-full bg-white text-[#ec3145] text-[9px] font-bold flex items-center justify-center">{totalItems}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Main Navbar ───────────────────────────────────────────────────────────────
export default function Navbar() {
  const cms = useCMS('navbar', DEFAULT);
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileOpenCat, setMobileOpenCat] = useState<string | null>(null);
  const [navbarHeight, setNavbarHeight] = useState(64);
  const megaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const { totalItems, setIsOpen } = useCart();

  const categories: NavCategory[] = cms.nav_categories ?? DEFAULT.nav_categories;
  const pageLinks: typeof DEFAULT.page_links = cms.page_links ?? DEFAULT.page_links;
  const [activeCat, setActiveCat] = useState<NavCategory>(categories[0]);

  // Keep activeCat in sync if CMS data loads after mount
  useEffect(() => {
    if (categories.length > 0) setActiveCat(categories[0]);
  }, [cms.nav_categories]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (headerRef.current) setNavbarHeight(headerRef.current.offsetHeight);
  }, [scrolled]);

  useEffect(() => {
    setDrawerOpen(false);
    setMobileOpenCat(null);
    setMegaOpen(false);
  }, [location.pathname]);

  const openMega = () => {
    if (megaTimerRef.current) clearTimeout(megaTimerRef.current);
    setMegaOpen(true);
  };
  const closeMega = () => {
    megaTimerRef.current = setTimeout(() => setMegaOpen(false), 180);
  };

  const linkCls = scrolled ? 'text-[#1C1C1C]/60 hover:text-[#ec3145]' : 'text-white/80 hover:text-white';

  return (
    <>
      <header ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/98 backdrop-blur-md border-b border-[#1C1C1C]/8' : 'bg-gradient-to-b from-black/55 to-transparent'}`}>
        <nav className="px-5 md:px-8 lg:px-14 flex items-center h-16 gap-4 md:gap-6 lg:gap-8">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 cursor-pointer">
            <img src={scrolled ? cms.logo_dark : cms.logo_light} alt="Phoenix" className="h-7 md:h-8 w-auto object-contain transition-all duration-500" />
          </Link>

          {/* Desktop: categories trigger */}
          <div className="hidden lg:block relative flex-shrink-0" onMouseEnter={openMega} onMouseLeave={closeMega}>
            <button className={`flex items-center gap-1.5 text-[11px] font-bold tracking-[0.12em] uppercase transition-colors duration-200 cursor-pointer whitespace-nowrap h-16 ${megaOpen ? (scrolled ? 'text-[#ec3145]' : 'text-white') : linkCls}`}>
              <i className="ri-apps-2-line text-sm"></i>
              {cms.label_categories}
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${megaOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {megaOpen && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#ec3145]"></span>}
          </div>

          <div className={`hidden lg:block w-px h-4 flex-shrink-0 ${scrolled ? 'bg-[#1C1C1C]/10' : 'bg-white/20'}`} />

          {/* Desktop: page links */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-6 flex-1">
            {pageLinks.map((link) => (
              <Link key={link.label} to={link.path} className={`text-[11px] font-semibold tracking-[0.1em] uppercase transition-colors duration-200 cursor-pointer whitespace-nowrap ${linkCls}`}>
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop: phones + cart */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-5 flex-shrink-0 ml-auto">
            <a href={cms.phone_office_href} className={`flex items-center gap-1.5 text-[11px] font-medium transition-colors duration-200 ${linkCls}`}>
              <i className="ri-phone-line text-xs"></i>{cms.phone_office}
            </a>
            <span className={`text-xs ${scrolled ? 'text-[#1C1C1C]/15' : 'text-white/20'}`}>|</span>
            <a href={cms.phone_mobile_href} className={`flex items-center gap-1.5 text-[11px] font-medium transition-colors duration-200 ${linkCls}`}>
              <i className="ri-smartphone-line text-xs"></i>{cms.phone_mobile}
            </a>
            <button onClick={() => setIsOpen(true)}
              className={`relative flex items-center justify-center w-8 h-8 transition-colors duration-200 cursor-pointer ml-1 ${scrolled ? 'text-[#1C1C1C]/60 hover:text-[#ec3145]' : 'text-white/80 hover:text-white'}`}
              aria-label={cms.label_cart}>
              <i className="ri-shopping-bag-line text-lg"></i>
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#ec3145] text-white text-[9px] font-bold flex items-center justify-center">{totalItems}</span>
              )}
            </button>
          </div>

          {/* Mobile: cart + hamburger */}
          <div className="lg:hidden flex items-center gap-3 ml-auto">
            <button onClick={() => setIsOpen(true)}
              className={`relative flex items-center justify-center w-8 h-8 transition-colors duration-200 cursor-pointer ${scrolled ? 'text-[#1C1C1C]/60' : 'text-white/80'}`}
              aria-label={cms.label_cart}>
              <i className="ri-shopping-bag-line text-lg"></i>
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#ec3145] text-white text-[9px] font-bold flex items-center justify-center">{totalItems}</span>
              )}
            </button>
            <button className="w-9 h-9 flex flex-col items-center justify-center gap-1.5 cursor-pointer" onClick={() => setDrawerOpen(true)} aria-label="მენიუ">
              <span className={`block h-px w-6 ${scrolled ? 'bg-[#1C1C1C]' : 'bg-white'}`} />
              <span className={`block h-px w-6 ${scrolled ? 'bg-[#1C1C1C]' : 'bg-white'}`} />
              <span className={`block h-px w-4 self-start ${scrolled ? 'bg-[#1C1C1C]' : 'bg-white'}`} />
            </button>
          </div>
        </nav>

        {/* Desktop Mega Panel */}
        <div className="hidden lg:block relative" onMouseEnter={openMega} onMouseLeave={closeMega}>
          <MegaPanel
            open={megaOpen}
            activeCat={activeCat}
            categories={categories}
            onCatHover={setActiveCat}
            onClose={closeMega}
            navbarHeight={navbarHeight}
            labelAll={cms.label_all}
            labelSubcats={cms.label_subcats}
          />
        </div>
      </header>

      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        openCat={mobileOpenCat}
        setOpenCat={setMobileOpenCat}
        onCartOpen={() => setIsOpen(true)}
        totalItems={totalItems}
        cms={cms}
        categories={categories}
        pageLinks={pageLinks}
      />
    </>
  );
}
